import { Injectable, inject } from '@angular/core';
import { IndexedDbStorageService } from './indexed-db-storage.service';

const DEVICE_ID_KEY = 'tops-offline.deviceId';

/**
 * Stable per-installation device identifier.
 *
 * <p>Required by all three audit stories. Persisted in IndexedDB rather than localStorage so it
 * survives alongside the rest of the offline data, and generated once so the same physical device
 * is recognisable across sessions and sign-ins.
 */
@Injectable({ providedIn: 'root' })
export class DeviceIdentityService {
  private readonly storage = inject(IndexedDbStorageService);
  private cachedDeviceId: string | null = null;

  async deviceId(): Promise<string> {
    if (this.cachedDeviceId) return this.cachedDeviceId;

    await this.storage.ready();
    let deviceId = this.storage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = this.createId();
      this.storage.setItem(DEVICE_ID_KEY, deviceId);
    }
    this.cachedDeviceId = deviceId;
    return deviceId;
  }

  /** Synchronous best-effort for call sites that cannot await, such as HTTP interceptors. */
  deviceIdSync(): string {
    if (this.cachedDeviceId) return this.cachedDeviceId;

    const stored = this.storage.getItem(DEVICE_ID_KEY);
    if (stored) {
      this.cachedDeviceId = stored;
      return stored;
    }

    const created = this.createId();
    this.cachedDeviceId = created;
    this.storage.setItem(DEVICE_ID_KEY, created);
    return created;
  }

  deviceInfo(): string {
    const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
    const shell = this.isElectronShell() ? 'electron' : 'browser';
    return [shell, platform, navigator.userAgent].filter(Boolean).join(' | ').slice(0, 512);
  }

  private isElectronShell(): boolean {
    return /electron/i.test(navigator.userAgent) || globalThis.location?.protocol === 'tops-offline:';
  }

  private createId(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi?.randomUUID) {
      return cryptoApi.randomUUID();
    }

    throw new Error('Secure device identifier generation is unavailable.');
  }
}
